import React, { useState, useEffect, useContext } from 'react';
import { User, Clock, CheckCircle2, XCircle, AlertCircle, Info, CreditCard, MapPin, DollarSign, TrendingUp, RefreshCw, Filter, Calendar, Package, CheckCircle, AlertTriangle } from 'lucide-react';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';

const DesignerProfile = () => {
  const { authToken } = useContext(AuthContext);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentFilters, setPaymentFilters] = useState({
    status: 'processed',  // Transfers use 'processed' status
    fromDate: '',
    toDate: ''
  });
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'payments'

  useEffect(() => {
    fetchDesignerData();
  }, [authToken]);

  const fetchDesignerData = async () => {
    try {
      setLoading(true);

      // Fetch designer profile
      const profileResponse = await axios.get(`${API_BASE_URL}/api/designer/profile/`, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      setProfileData(profileResponse.data.data);

    } catch (error) {
      console.error('Error fetching designer data:', error);
      if (error.response?.status === 404) {
        Swal.fire({
          title: 'No Designer Profile',
          text: 'Please complete your KYC first to view your designer profile.',
          icon: 'info',
          confirmButtonColor: '#ea580c'
        });
      } else {
        Swal.fire('Error', 'Failed to fetch designer profile data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setPaymentLoading(true);

      const params = new URLSearchParams();
      if (paymentFilters.status !== 'all') params.append('status', paymentFilters.status);
      if (paymentFilters.fromDate) params.append('from_date', new Date(paymentFilters.fromDate).getTime() / 1000);
      if (paymentFilters.toDate) params.append('to_date', new Date(paymentFilters.toDate).getTime() / 1000);

      const response = await axios.get(`${API_BASE_URL}/api/designer/payments/?${params}`, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        setPayments(response.data.data.transfers);
        setPaymentStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      Swal.fire('Error', 'Failed to fetch payment data', 'error');
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'payments' && profileData) {
      fetchPayments();
    }
  }, [activeTab, paymentFilters, profileData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'under_review':
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPaymentStatusBadge = (status) => {
    // Simplify status to just "Settled" or "Processing"
    let displayStatus, bgColor, textColor, icon;

    if (status === 'processed') {
      displayStatus = 'Settled';
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      icon = CheckCircle;
    } else if (status === 'pending' || status === 'created') {
      displayStatus = 'Processing';
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      icon = Clock;
    } else if (status === 'failed') {
      displayStatus = 'Failed';
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      icon = XCircle;
    } else {
      displayStatus = status;
      bgColor = 'bg-gray-100';
      textColor = 'text-gray-800';
      icon = AlertCircle;
    }

    const Icon = icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor}`}>
        <Icon className="w-4 h-4 mr-1.5" />
        {displayStatus}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-600">Loading designer profile...</p>
          </div>
        </div>
      } />
    );
  }

  if (!profileData) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md">
            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Designer Profile</h2>
            <p className="text-gray-600 mb-6">Please complete your KYC first to view your designer profile.</p>
            <button
              onClick={() => window.location.href = '/designer-onboarding'}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Complete KYC
            </button>
          </div>
        </div>
      } />
    );
  }

  return (
    <Base Content={
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white rounded-full p-3">
                    <User className="h-8 w-8 text-orange-500" />
                  </div>
                  <div className="text-white">
                    <h1 className="text-3xl font-bold">{profileData.name}</h1>
                    <p className="text-orange-100">{profileData.email}</p>
                    <p className="text-orange-100">{profileData.phone}</p>
                  </div>
                </div>
                <div className="text-right text-white">
                  <div className="flex items-center space-x-2 mb-2">
                    {getStatusIcon(profileData.onboarding_status)}
                    <span className="font-semibold capitalize">{profileData.onboarding_status}</span>
                  </div>
                  <p className="text-sm text-orange-100">Member since {formatDate(profileData.created_at)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>Overview</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'payments'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4" />
                    <span>Payment History</span>
                  </div>
                </button>
              </nav>
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                  <div className="space-y-6">
                  {/* KYC Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Personal Details</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{profileData.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Email:</span>
                            <span className="font-medium">{profileData.email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium">{profileData.phone}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Business Type:</span>
                            <span className="font-medium capitalize">{profileData.business_type}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Tax & Banking</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">PAN:</span>
                            <span className="font-medium font-mono">{profileData.pan || 'Not provided'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">GSTIN:</span>
                            <span className="font-medium font-mono">{profileData.gstin || 'Not provided'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Bank Account:</span>
                            <span className="font-medium font-mono">
                              {profileData.bank_account_number ? `****${profileData.bank_account_number.slice(-4)}` : 'Not provided'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">IFSC:</span>
                            <span className="font-medium font-mono">{profileData.ifsc_code || 'Not provided'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="bg-gray-50 rounded-lg p-4 mt-6">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                        Business Address
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-600 block">Street Address Line 1:</span>
                          <span className="font-medium">{profileData.street_address_1 || 'Not provided'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 block">Street Address Line 2:</span>
                          <span className="font-medium">{profileData.street_address_2 || 'Not provided'}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-gray-600 block">City:</span>
                            <span className="font-medium">{profileData.city || 'Not provided'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 block">State:</span>
                            <span className="font-medium">{profileData.state || 'Not provided'}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-gray-600 block">Postal Code:</span>
                            <span className="font-medium">{profileData.postal_code || 'Not provided'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 block">Country:</span>
                            <span className="font-medium">{profileData.country || 'IN'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Account Status */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(profileData.onboarding_status)}
                          <div>
                            <p className="font-medium capitalize">{profileData.onboarding_status}</p>
                            <p className="text-sm text-gray-600">
                              {profileData.onboarding_status === 'approved' && 'Your account is fully verified and you can receive payouts.'}
                              {profileData.onboarding_status === 'under_review' && 'Your KYC documents are being reviewed.'}
                              {profileData.onboarding_status === 'rejected' && 'Your KYC was rejected. Please resubmit with correct information.'}
                              {profileData.onboarding_status === 'pending' && 'Please complete your KYC verification.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Razorpay Integration Status */}
                  {(profileData.razorpay_account_id || profileData.razorpay_stakeholder_id || profileData.razorpay_bank_account_id || profileData.razorpay_product_id) && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <CreditCard className="h-5 w-5 mr-2 text-orange-500" />
                        Razorpay Payment Integration
                      </h3>
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {profileData.razorpay_account_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Linked Account ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_account_id}</p>
                            </div>
                          )}
                          {profileData.razorpay_stakeholder_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Stakeholder ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_stakeholder_id}</p>
                            </div>
                          )}
                          {profileData.razorpay_bank_account_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_bank_account_id}</p>
                            </div>
                          )}
                          {profileData.razorpay_product_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Product Configuration ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_product_id}</p>
                            </div>
                          )}
                        </div>

                        {profileData.razorpay_activation_status && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200 mb-4">
                            <label className="block text-xs font-medium text-gray-600 mb-2">Razorpay Activation Status</label>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                              profileData.razorpay_activation_status === 'activated'
                                ? 'bg-green-100 text-green-800'
                                : profileData.razorpay_activation_status === 'needs_clarification'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {profileData.razorpay_activation_status === 'activated' && '✓ Activated - Ready to Receive Payments'}
                              {profileData.razorpay_activation_status === 'created' && '⏳ Under Review by Razorpay'}
                              {profileData.razorpay_activation_status === 'needs_clarification' && '⚠️ Needs Clarification'}
                            </span>
                          </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-blue-800">
                              <p className="font-semibold mb-1">How Payouts Work:</p>
                              <ul className="space-y-1 list-disc list-inside">
                                <li>When a customer completes an order, admin creates a payout request</li>
                                {/* <li>Platform commission (3%) is deducted from the order amount</li> */}
                                <li>After admin approval, the remaining amount (97%) is transferred to your linked bank account via Razorpay Route</li>
                                <li>You receive an email notification when the payout is processed</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment History Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              {paymentLoading ? (
                <div className="bg-white rounded-xl shadow-lg p-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                    <p className="text-lg font-medium text-gray-600">Loading payment history...</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Stats Cards */}
                  {paymentStats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Total Transferred */}
                      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Transferred</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">
                              ₹{paymentStats.total_transferred?.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                            </p>
                          </div>
                          <div className="bg-blue-100 rounded-full p-3">
                            <TrendingUp className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                      </div>

                      {/* Commission */}
                      {/* <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Platform Commission (3%)</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">
                              ₹{paymentStats.commission_amount?.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                            </p>
                          </div>
                          <div className="bg-red-100 rounded-full p-3">
                            <AlertCircle className="w-6 h-6 text-red-600" />
                          </div>
                        </div>
                      </div> */}

                      {/* Net Earnings */}
                      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Your Earnings</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">
                              ₹{paymentStats.total_transferred?.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                            </p>
                          </div>
                          <div className="bg-green-100 rounded-full p-3">
                            <DollarSign className="w-6 h-6 text-green-600" />
                          </div>
                        </div>
                      </div>

                      {/* Transfer Count */}
                      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Processed Transfers</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">
                              {paymentStats.payment_count}
                            </p>
                          </div>
                          <div className="bg-orange-100 rounded-full p-3">
                            <Package className="w-6 h-6 text-orange-600" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Filter className="w-5 h-5 mr-2 text-orange-500" />
                        Filters
                      </h3>
                      <button
                        onClick={fetchPayments}
                        className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Status
                        </label>
                        <select
                          value={paymentFilters.status}
                          onChange={(e) => setPaymentFilters({ ...paymentFilters, status: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="processed">Settled</option>
                          <option value="all">All</option>
                          <option value="pending">Processing</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      {/* From Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          From Date
                        </label>
                        <input
                          type="date"
                          value={paymentFilters.fromDate}
                          onChange={(e) => setPaymentFilters({ ...paymentFilters, fromDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      {/* To Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          To Date
                        </label>
                        <input
                          type="date"
                          value={paymentFilters.toDate}
                          onChange={(e) => setPaymentFilters({ ...paymentFilters, toDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Transfer List */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Transfer Transactions</h3>

                    {payments.length === 0 ? (
                      <div className="text-center py-12">
                        <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 text-lg">No transfer transactions found</p>
                        <p className="text-gray-500 text-sm mt-2">
                          {paymentFilters.status !== 'all' || paymentFilters.fromDate || paymentFilters.toDate
                            ? 'Try adjusting your filters'
                            : 'Your transfer history will appear here once you receive payouts'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {payments.map((payment) => (
                          <div
                            key={payment.id}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                {/* Transfer ID */}
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold text-gray-900 text-lg">{payment.id}</h4>
                                  {getPaymentStatusBadge(payment.status)}
                                </div>

                                {/* Order ID */}
                                <p className="text-sm text-gray-600 mb-1">
                                  Order: <span className="font-medium text-gray-800">{payment.source || 'N/A'}</span>
                                </p>

                                {/* Date */}
                                <p className="text-xs text-gray-500">
                                  {payment.created_at ? new Date(payment.created_at * 1000).toLocaleDateString('en-IN', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : 'N/A'}
                                </p>
                              </div>

                              {/* Amount */}
                              <div className="text-right ml-4">
                                <p className="text-2xl font-bold text-gray-900">
                                  ₹{payment.amount?.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    } />
  );
};

export default DesignerProfile;
